if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.selectedIndex = -1;
      this.boundHandlers = {
        triggerClick: this.handleTriggerClick.bind(this),
        optionClick: this.handleOptionClick.bind(this),
        keydown: this.handleKeydown.bind(this),
        outsideClick: this.handleOutsideClick.bind(this),
      };
    }

    connectedCallback() {
      this.initializeElements();
      this.setupEventListeners();
      this.updateSelectedIndex();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    initializeElements() {
      this.trigger = this.querySelector('.custom-select__trigger');
      this.dropdown = this.querySelector('.custom-select__dropdown');
      this.valueDisplay = this.querySelector('.custom-select__value');
      this.arrow = this.querySelector('.custom-select__arrow');
      this.hiddenInput = this.querySelector('.custom-select__input');
      this.options = this.querySelectorAll('.custom-select__option');
    }

    setupEventListeners() {
      this.trigger?.addEventListener('click', this.boundHandlers.triggerClick);
      this.options.forEach((option) => {
        option.addEventListener('click', this.boundHandlers.optionClick);
      });
      this.addEventListener('keydown', this.boundHandlers.keydown);
    }

    removeEventListeners() {
      this.trigger?.removeEventListener('click', this.boundHandlers.triggerClick);
      this.options.forEach((option) => {
        option.removeEventListener('click', this.boundHandlers.optionClick);
      });
      document.removeEventListener('click', this.boundHandlers.outsideClick);
    }

    handleTriggerClick(event) {
      event.preventDefault();
      this.isOpen ? this.closeDropdown() : this.openDropdown();
    }

    handleOptionClick(event) {
      event.preventDefault();
      const option = event.currentTarget;
      this.selectOption(option);
      this.closeDropdown();
    }

    handleKeydown(event) {
      if (!this.isOpen && !['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        return;
      }

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.isOpen ? this.selectFocusedOption() : this.openDropdown();
          break;
        case 'Escape':
          this.closeDropdown();
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.isOpen ? this.focusNextOption() : this.openDropdown();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.isOpen ? this.focusPreviousOption() : this.openDropdown();
          break;
      }
    }

    handleOutsideClick(event) {
      if (!this.contains(event.target)) {
        this.closeDropdown();
      }
    }

    openDropdown() {
      this.isOpen = true;
      this.dropdown?.classList.remove('hidden');
      this.trigger?.setAttribute('aria-expanded', 'true');
      this.arrow?.classList.add('rotate-180');
      document.addEventListener('click', this.boundHandlers.outsideClick);
      this.focusSelectedOption();
    }

    closeDropdown() {
      this.isOpen = false;
      this.dropdown?.classList.add('hidden');
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.arrow?.classList.remove('rotate-180');
      document.removeEventListener('click', this.boundHandlers.outsideClick);
      this.trigger?.focus();
    }

    selectOption(option) {
      this.clearSelection();
      option.setAttribute('aria-selected', 'true');
      this.updateValue(option.dataset.value);
      this.updateDisplay(option);
      this.updateSelectedIndex();

      // Trigger change event for language-switcher compatibility
      const changeEvent = new CustomEvent('change', { bubbles: true });
      this.dispatchEvent(changeEvent);
    }

    clearSelection() {
      this.options.forEach((option) => {
        option.setAttribute('aria-selected', 'false');
      });
    }

    updateValue(value) {
      if (this.hiddenInput) {
        this.hiddenInput.value = value;
      }
    }

    updateDisplay(option) {
      if (this.valueDisplay) {
        this.valueDisplay.innerHTML = option.innerHTML;
      }
    }

    updateSelectedIndex() {
      this.selectedIndex = Array.from(this.options).findIndex(
        (option) => option.getAttribute('aria-selected') === 'true'
      );
    }

    focusSelectedOption() {
      if (this.selectedIndex >= 0) {
        this.options[this.selectedIndex]?.focus();
      } else {
        this.options[0]?.focus();
      }
    }

    focusNextOption() {
      const nextIndex = Math.min(this.selectedIndex + 1, this.options.length - 1);
      this.options[nextIndex]?.focus();
    }

    focusPreviousOption() {
      const prevIndex = Math.max(this.selectedIndex - 1, 0);
      this.options[prevIndex]?.focus();
    }

    selectFocusedOption() {
      const focused = this.querySelector('.custom-select__option:focus');
      if (focused) {
        this.selectOption(focused);
        this.closeDropdown();
      }
    }

    // Native select API compatibility
    get value() {
      return this.hiddenInput?.value || '';
    }

    set value(val) {
      const option = Array.from(this.options).find((opt) => opt.dataset.value === val);
      if (option) {
        this.selectOption(option);
      }
    }

    get selectedOptions() {
      const selected = this.querySelector('.custom-select__option[aria-selected="true"]');
      return selected ? [selected] : [];
    }

    set innerHTML(html) {
      if (this.dropdown) {
        this.dropdown.innerHTML = html;
        this.options = this.querySelectorAll('.custom-select__option');
        this.setupOptionListeners();
      }
    }

    appendChild(element) {
      this.dropdown?.appendChild(element);
      this.options = this.querySelectorAll('.custom-select__option');
      this.setupOptionListeners();
    }

    setupOptionListeners() {
      this.options.forEach((option) => {
        option.addEventListener('click', this.boundHandlers.optionClick);
      });
    }
  }

  customElements.define('custom-select', CustomSelect);
}
